<template>
  <v-app>
    <component :is="layout" v-show="init" :isTwilioInitialized="isTwilioInitialized"/>
    <loading v-if="!currentPathname.includes('chat-message')" :active="isCallingApi" />
  </v-app>
</template>
<script>
import BaseLayout from './components/layouts/BaseLayout'
import MainLayout from './components/layouts/MainLayout'
import twilioDevice from '@/utils/twilioDevice'
import 'vue-loading-overlay/dist/vue-loading.css'
import Loading from 'vue-loading-overlay'
import { bus } from '@/main'
import { mapGetters, mapMutations } from 'vuex'
import { RepositoryFactory } from '@/api/RepositoryFactory'
import moment from 'moment'
import { throttle } from 'lodash'
import Helper from './utils/Helper'
import {
  STATUS_AFTER_CALL,
  STATUS_OFFLINE,
  STATUS_ONLINE,
  STATUS_PENDING,
  STATUS_SPEAKING
} from './constants/status'
import { getCloseCallWindowValue } from '@/utils/storage-event'

const UserRepository = RepositoryFactory.get('users')
const PhoneAccountRepository = RepositoryFactory.get('phoneAccount')
const CompanyRepository = RepositoryFactory.get('company')
const TwilioRepository = RepositoryFactory.get('twilio')

const INCOMING_PREFIX = '着信-'
const INCOMING_TRANSFER_PREFIX = '転送着信-'

const REFRESH_PAGE = 1
export default {
  name: 'App',
  components: {
    'base-layout': BaseLayout,
    'main-layout': MainLayout,
    Loading
  },
  data () {
    return {
      init: false,
      twilioInitializing: false,
      workerTaskRouter: null,
      isSetupTaskRouterEvent: false,
      isSetupTwilioEvent: false,
      isRefreshingToken: false,
      isTwilioInitialized: false,
      incomingCallNotification: null,
      audio: null,
      disconnectSocketInterval: null,
      disconnectTaskRouterInterval: null,
      internetDisconnect: null,
      refreshTimeline: 10,
      onlineStatusAfterCalling: null,
      timeoutPopupDisonnectInternet: null,
      timeoutRedirectChatLine: null
    }
  },
  sockets: {
    logoutOtherDevices: async function () {
      await this.$swal.fire({
        title: '',
        text: '他の端末でログインしていたため、該当の端末をログアウトさせました',
        confirmButtonText: 'OK',
        allowOutsideClick: false
      })
    },
    otherDeviceLogin: async function () {
      await this.logout(true)
    },
    deleteAgent: async function () {
      await this.logout()
    },
    disableAgent: async function (data) {
      if (data) {
        if (parseInt(this.userInfo.id) === parseInt(data.id) && (this.userInfo.email === data.email) && parseInt(this.userInfo.company_id) === parseInt(data.company_id)) {
          await this.logout(false, true)
        }
      }
    },
    connect: async function () {
      console.log('Socket backend connected')
      if (this.token) {
        this.$socket.emit('onlineSocket', this.token)
      }
      clearInterval(this.disconnectSocketInterval)
      await this.hideDisconnectedModal()
      if (this.isExpiredTaskRouter) {
        const newToken = await this.refreshTwilioToken()
        if (newToken) {
          this.isRefreshingToken = true
          this.workerTaskRouter.updateToken(newToken.taskRouterToken)
          this.setupListenerTaskRouter(this.workerTaskRouter)
          await twilioDevice.setup(newToken.twilioClientAccessToken, { debug: true })
          this.isRefreshingToken = false
          this.isTwilioInitialized = true
          console.log('REFRESH TOKEN IN CASE SLEEP', Date.now() / 1000)
        }
      }
    },
    disconnect: async function () {
      this.stopAudio()
      if (!localStorage.getItem('login')) return
      console.log('debug log: socket backend disconnected')
      await this.closeCallingAndShowModal({
        wait: 5 // need to allow 5 sec to display the disconnected error in the calling screen
      })
      clearInterval(this.disconnectSocketInterval)
      this.disconnectSocketInterval = setInterval(async () => {
        this.$socket.connect()
        if (localStorage.getItem('lastStatus') !== this.inCallStatus.sid || localStorage.getItem('lastStatus') !== this.holdingStatus.sid) {
          this.stopAudio()
          await this.changeStatusTaskRouter({ sid: localStorage.getItem('lastStatus') })
        }
      }, 1000)
    },
    'reservation-canceled': async function (reservationSid) {
      console.log('RESERVATION CANCELED CRM')
      if (this.currentReservation && this.currentReservation.sid === reservationSid) {
        this.stopAudio()
        this.$store.commit('reservation/RESET_WORKSPACE')
        if (this.currentReservation && this.currentReservation.task) {
          this.$socket.emit('leave-task-room', this.currentReservation.task.sid)
        }
      }
    },
    'new-line-message': async function (data) {
      const { line_phone } = data
      const pathname = window.location.pathname
      if (
        line_phone.last_message &&
        line_phone.last_message.from_type.toLowerCase() === 'line-user' &&
        !pathname.includes('end-call') &&
        !pathname.includes('calling')
      ) {
        this.showNewMessageNotification(line_phone)
      }
    },
    logout: function () {
      this.logout()
    }
  },
  computed: {
    currentPathname() {
      return localStorage.getItem('currentPath') ?? window.location.pathname
    },
    layout () {
      return (this.$route.meta.layout || 'base') + '-layout'
    },
    offlineStatus () {
      return this.allStatuses.find(status => status.status === STATUS_OFFLINE)
    },
    isExpiredTaskRouter() {
      if (!this.taskRouterToken) {
        return false
      }
      const parsedToken = this.parseJwt(this.taskRouterToken)
      return (parsedToken.exp + this.refreshTimeline) < Date.now() / 1000
    },
    ...mapGetters({
      twilioToken: 'user/getTwilioClientAccessToken',
      taskRouterToken: 'user/getTaskRouterToken',
      isCallingApi: 'common/isCallingApi',
      token: 'user/getToken',
      connection: 'twilioDevice/connection',
      worker: 'reservation/worker',
      userInfo: 'user/getUserInfo',
      isRemember: 'user/getRemember',
      currentReservation: 'reservation/getReservation',
      currentTask: 'reservation/task',
      isCalling: 'twilioDevice/isCalling',
      isACast: 'user/getIsACast',
      currentConferences: 'callMonitoring/getCurrentConferences',
      hasModalNetworkError: 'common/getHasModalNetworkError'
    }),
    ...mapGetters('status', ['allStatuses']),
    inCallStatus () {
      if (!this.allStatuses) {
        return null
      }
      return this.allStatuses.find((status) => status.status === STATUS_SPEAKING)
    },
    afterCallStatus () {
      if (!this.allStatuses) {
        return null
      }
      return this.allStatuses.find((status) => status.status === STATUS_AFTER_CALL)
    },
    onlineStatus () {
      if (!this.allStatuses) {
        return null
      }
      return this.allStatuses.find((status) => status.status === STATUS_ONLINE)
    },
    holdingStatus () {
      if (!this.allStatuses) {
        return null
      }
      return this.allStatuses.find((status) => status.status === STATUS_PENDING)
    }
  },
  async created() {
    if (this.userInfo && Object.keys(this.userInfo).length) {
      if (this.userInfo.company_id && (!this.userInfo.company || (this.userInfo.company && !this.userInfo.company.role))) {
        const company = await CompanyRepository.findOneById(this.userInfo.company_id)
        if (company.success) {
          this.$store.commit('user/SET_USER_INFO', {
            ...this.userInfo,
            company: company.data
          })
          this.$store.commit('user/SET_USER_COMPANY', company.data)
        }
      }

      if (!this.isACast && this.taskRouterToken && this.token) {
        TwilioRepository.updateExpressionWorkflow()
      }

      const pathname = window.location.pathname
      if (
        this.userInfo.company &&
        this.isRemember &&
        !this.userInfo.company.disabled_phone &&
        !pathname.includes('end-call') &&
        !pathname.includes('calling') &&
        !pathname.includes('call-monitoring') &&
        !pathname.includes('calling') &&
        !pathname.includes('dial')
      ) {
        this.$swal.fire({
          title: '',
          text: 'ログインしました。着信音を有効にするため、クリックしてください',
          icon: 'success',
          confirmButtonText: 'OK'
        })
      }
    }

    this.init = true
    this.$root.$on('initTwilio', this.initTwilio)
    this.$root.$on('reInitTwilio', this.reInitTwilio)
    this.$root.$on('destroyTwilio', this.destroyTwilio)
    this.$root.$on('changeStatusTaskRouter', this.changeStatusTaskRouter)
    this.$root.$on('endIncomingCall', this.endIncomingCall)
    this.$root.$on('stopAudio', this.stopAudio)
    this.$root.$on('logout', this.logout)
    this.$root.$on('playAudio', this.playAudio)
    this.$store.commit('common/SET_IS_CALLING_API', false)
    await this.initAudio()
    this.$socket.on('reconnect', (attemptNumber) => {
      if (this.token) {
        this.$socket.emit('onlineSocket', this.token)
        this.$socket.emit('join-room', `company_${this.userInfo.company_id}`)
      }
    })
    window.addEventListener('storage', async (e) => {
      if (e.newValue == null) return
      if (e.key === 'logout') {
        this.$socket.emit('offlineSocket')
        await this.destroyTwilio()
        this.isSetupTwilioEvent = false
        this.isSetupTaskRouterEvent = false
        if (this.$route.name === 'Login') return
        await this.$router.push({ name: 'Login' })
      }
      if (e.key === 'login') {
        location.reload()
      }
      if (e.key === 'closeCalling') {
        localStorage.removeItem('closeCalling')
        if (localStorage.getItem('reservationClosed')) {
          this.completeTask()
          await this.changeStatusTaskRouter(this.onlineStatus)
          localStorage.removeItem('reservationClosed')
          return
        }
        this.completeTask()

        if (this.userInfo?.company) {
          const userCompany = this.userInfo?.company
          if (userCompany.end_call_status === 'offline') {
            await this.changeStatusTaskRouter(this.onlineStatus)
          } else {
            await this.changeStatusTaskRouter(this.afterCallStatus)
          }

          localStorage.setItem('end_call_status', userCompany.end_call_status)
        }
      }
      if (e.key === 'closeCallMonitoring') {
        await this.$store.commit('callMonitoring/SET_COACHING_CONFERENCE')
        localStorage.removeItem('closeCallMonitoring')
      }
      if (e.key === 'notAllowAnswerCall') {
        localStorage.removeItem('notAllowAnswerCall')
        await this.$swal.fire({
          title: '',
          text: this.$t('messages.call_ended_another_answered'),
          confirmButtonText: 'OK',
          allowOutsideClick: false
        }).then((result) => {
          if (result.isConfirmed) {
            this.stopAudio()
          }
        })
        await this.changeStatusTaskRouter(this.onlineStatus)
      }
    })
    window.addEventListener('offline', async (e) => {
      this.internetDisconnect = true
      console.log('debug log: internet disconnected')
      if (this.timeoutPopupDisonnectInternet) {
        clearTimeout(this.timeoutPopupDisonnectInternet)
      }
      this.timeoutPopupDisonnectInternet = setTimeout(async () => {
        if (!this.hasModalNetworkError) {
          this.$swal.fire({
            title: '',
            text: this.$t('messages.network_error'),
            showConfirmButton: false,
            allowOutsideClick: false
          }).then(() => {
            window.location.reload()
          })
          this.setHasModalNetworkError(true)
        }
      }, 5000)
    })
    window.addEventListener('online', async (e) => {
      if (!this.internetDisconnect) return
      if (this.timeoutPopupDisonnectInternet) {
        this.setHasModalNetworkError(false)
        clearTimeout(this.timeoutPopupDisonnectInternet)
      }
      this.internetDisconnect = false
      this.$swal.close()
      if (!this.isACast) {
        location.reload()
      }
    })
  },
  watch: {
    worker(newVal) {
      if (newVal.activitySid !== this.offlineStatus.sid) {
        localStorage.setItem('lastStatus', newVal.activitySid)
      }
      if (localStorage.getItem('lastStatus')) {
        throttle(() => {
          this.changeStatusTaskRouter({ sid: localStorage.getItem('lastStatus') })
        }, 1000)
      } else if (this.userInfo.company.end_call_status === 'offline' && this.onlineStatus) {
        this.changeStatusTaskRouter({ sid: this.onlineStatus.sid })
      } else if (this.userInfo.company.end_call_status !== 'offline' && this.afterCallStatus) {
        this.changeStatusTaskRouter({ sid: this.afterCallStatus.sid })
      }
    },
    isCalling(newVal) {
      this.stopAudio()
      if (!newVal) {
        if (this.currentConferences.length) {
          this.$store.commit('callMonitoring/SET_CURRENT_CONFERENCES', [])
        }
      }
    }
  },
  mounted() {
    const _this = this
    this.initTwilio()
    if (this.token) {
      this.$socket.open()
      this.$socket.emit('onlineSocket', this.token)
      this.$socket.emit('join-room', `company_${this.userInfo.company_id}`)
    }
    if (this.onlineStatusAfterCalling) clearInterval(this.onlineStatusAfterCalling)
    if (!this.isACast && this.taskRouterToken && this.token && this.userInfo) {
      if (this.userInfo.is_must_logout) {
        this.logout()
      }
      this.onlineStatusAfterCalling = setInterval(async () => {
        if (_this.userInfo.worker_sid && localStorage.getItem('passwordNotChanged')) {
          const currentUser = await UserRepository.getByWorkerSid(_this.userInfo.worker_sid, true)
          if (currentUser.success && currentUser.data) {
            _this.$store.commit('user/SET_USER_INFO', {
              ..._this.userInfo,
              password_changed: currentUser.data.password_changed
            })
          }
          if (_this.userInfo.password_changed) {
            localStorage.removeItem('passwordNotChanged')
            if (localStorage.getItem('lastStatus')) {
              _this.changeStatusTaskRouter({ sid: localStorage.getItem('lastStatus') })
            }
          }
        }
        if (
          !_this.isCalling &&
          _this.inCallStatus &&
          ((_this.worker?.activitySid === _this.inCallStatus?.sid && !_this.currentTask) ||
          (_this.worker?.activitySid === _this.inCallStatus?.sid))
        ) {
          _this.stopAudio()
          await _this.changeStatusTaskRouter({ sid: localStorage.getItem('lastStatus') })
        }
        if (this.internetDisconnect) {
          _this.$socket.connect()
        }
      }, 10000)

      if (this.timeoutRedirectChatLine) {
        clearInterval(this.timeoutRedirectChatLine)
      }
      this.timeoutRedirectChatLine = setInterval(() => {
        const pathname = window.location.pathname
        if (
          localStorage.getItem('lineUserFocusDefault') &&
          !pathname.includes('end-call') &&
          !pathname.includes('calling')
        ) {
          this.$router.push({
            name: 'LineChat',
            params: {
              lineUserFocusDefault: localStorage.getItem('lineUserFocusDefault')
            }
          })
        }
      }, 1000)
    }
  },
  methods: {
    initTwilio () {
      if (this.twilioToken && this.twilioToken !== 'undefined' &&
        this.taskRouterToken && this.taskRouterToken !== 'undefined' &&
        this.canSetupTwilio()
      ) {
        const storage = localStorage.getItem('remember') ? localStorage : sessionStorage
        if (!storage.getItem('twilio_client_access_token') || !storage.getItem('task_router_token')) return
        this.twilioInitializing = true
        twilioDevice.setup(this.twilioToken, { debug: true, edge: 'tokyo' })

        this.setupListenerDevice(twilioDevice)

        this.setupTaskRouter()
      }
    },
    setupTaskRouter() {
      try {
        if (!this.workerTaskRouter) {
          /* global Twilio */
          this.workerTaskRouter = new Twilio.TaskRouter.Worker(this.taskRouterToken)
        }
        this.setupListenerTaskRouter(this.workerTaskRouter)
      } catch (e) {
        console.log('Error on setupTaskRouter', e)
      }
    },
    canSetupTwilio () {
      if (!twilioDevice.isInitialized) {
        return true
      }
      return (
        !this.twilioInitializing &&
        twilioDevice.status() === STATUS_OFFLINE
      )
    },
    async reInitTwilio () {
      await twilioDevice.setup(this.twilioToken, { debug: true })
      await this.workerTaskRouter.updateToken(this.taskRouterToken)
    },
    async destroyTwilio () {
      twilioDevice._events = {}
      twilioDevice.destroy()
      if (this.workerTaskRouter) {
        this.workerTaskRouter._events = null
      }
      this.workerTaskRouter = null
      await this.$store.commit('reservation/RESET_WORKER')
      await this.$store.commit('common/SET_HAS_CALLING_TABS', false)
    },
    setupListenerDevice (twilioDevice) {
      const self = this
      if (self.isSetupTwilioEvent) return
      self.isSetupTwilioEvent = true
      twilioDevice.on('ready', function () {
        self.twilioInitializing = false
        bus.$emit('twilioReady')
        self.hideDisconnectedModal()
      })
      twilioDevice.on('error', async function (error) {
        console.log(error)
        // 31205: JWT Token Expired, 32102: Access Token expired or expiration date invalid
        if (error.code === 31205 || error.code === 31202) {
          if (self.isRefreshingToken) return
          self.isRefreshingToken = true
          const newToken = await self.refreshTwilioToken()
          if (!newToken) return
          await twilioDevice.setup(newToken.twilioClientAccessToken, { debug: true })
          await self.workerTaskRouter.updateToken(newToken.taskRouterToken)
          self.setupListenerTaskRouter(self.workerTaskRouter)
          await self.changeStatusTaskRouter(this.onlineStatus)
          console.log('Twilio Device finish refresh token')
          self.isRefreshingToken = false
        } else if (error.code === 31000) {
          // network to twilio got disconnected
          console.log('-------------------twilioDevice disconnect')
        } else if (error.code === 31005) {
          console.log('debug log: twilio device has an error')
          await self.closeCallingAndShowModal()
          self.initTwilio()
        }
      })
    },
    async hideDisconnectedModal() {
      if (!this.internetDisconnect) return
      if (this.timeoutPopupDisonnectInternet) {
        clearTimeout(this.timeoutPopupDisonnectInternet)
      }
      this.internetDisconnect = false
      this.$swal.close()
      await this.changeStatusTaskRouter({ sid: localStorage.getItem('lastStatus') })
    },
    async closeCallingAndShowModal({ wait } = {}) {
      localStorage.removeItem('closeCallWindow')
      localStorage.setItem('closeCallWindow', getCloseCallWindowValue(wait))
      this.internetDisconnect = true
      if (this.timeoutPopupDisonnectInternet) {
        clearTimeout(this.timeoutPopupDisonnectInternet)
      }
      this.timeoutPopupDisonnectInternet = setTimeout(async () => {
        if (!this.hasModalNetworkError) {
          this.$swal.fire({
            title: '',
            text: this.$t('messages.network_error'),
            showConfirmButton: false,
            allowOutsideClick: false
          })
          this.setHasModalNetworkError(true)
        }
      }, 5000)
    },
    setupListenerTaskRouter (worker) {
      const self = this
      if (self.isSetupTaskRouterEvent) return
      self.isSetupTaskRouterEvent = true
      worker._events = null
      worker.on('ready', async function (worker) {
        self.$store.commit('reservation/SET_WORKER', worker)
        bus.$emit('taskRouterReady')

        if (localStorage.getItem('lastStatus') !== worker.activitySid && !localStorage.getItem('passwordNotChanged')) {
          let status = { sid: localStorage.getItem('lastStatus') }
          if (performance.navigation.type !== REFRESH_PAGE) {
            status = self.onlineStatus
          }
          console.log('setupListenerTaskRouter: ', status)
          await self.changeStatusTaskRouter(status)
        } else {
          self.isTwilioInitialized = true
        }
      })
      worker.on('attributes.update', function (worker) {
        self.$store.commit('reservation/SET_WORKER', worker)
      })

      worker.on('reservation.created', async function (reservation) {
        console.log('TaskRouter Worker: reservation.created')
        console.log(reservation)
        const attributes = reservation.task.attributes
        if (!(attributes && (attributes.reciprocalType === 'transfer' || attributes.reciprocalType === 'file') && attributes.duration === 0)) {
          self.$store.commit('reservation/SET_RESERVATION', reservation)
          self.$store.commit('reservation/SET_TASK', reservation.task)
          self.$socket.emit('join-task-room', reservation.task.sid)
          await bus.$emit('reservationCreated')
          if (self.$route.name !== 'Calling') {
            const prefixTitle = (reservation.task.attributes?.type === 'call-transfer') ? INCOMING_TRANSFER_PREFIX : INCOMING_PREFIX
            const title = prefixTitle + await self.getPhoneAccount(reservation.task.attributes.from)
            self.incomingCallNotification = Helper.displayNotification(title)
            if (!self.isCalling) {
              await self.audio.play()
            }
          }
        }
      })

      worker.on('reservation.accepted', function (reservation) {
        console.log('TaskRouter Worker: reservation.accepted')
        console.log(reservation)
        self.$store.commit('twilioDevice/SET_CALL_SID', reservation.task.attributes.call_sid)
        self.$store.commit('twilioDevice/SET_CALL_FROM', reservation.task.attributes.from)
        self.$store.commit('twilioDevice/SET_CONFERENCE_ID', reservation.task.attributes.conference.sid)
        self.$store.commit('reservation/SET_TASK', reservation.task)
        self.$store.commit('reservation/REMOVE_RESERVATION')
        self.stopAudio()
        self.changeStatusTaskRouter(self.inCallStatus)
        self.incomingCallNotification?.close()
      })
      worker.on('reservation.timeout', function (reservation) {
        console.log('TaskRouter Worker: reservation.timeout')
        if (reservation && reservation.task) {
          self.$socket.emit('leave-task-room', reservation.task.sid)
        }
        self.$store.commit('reservation/RESET_WORKSPACE')
        self.stopAudio()
        self.incomingCallNotification?.close()
      })
      worker.on('reservation.rescinded', async function (reservation) {
        self.$store.commit('reservation/PUSH_INVALID_RESERVATION', reservation.sid)
        console.log('TaskRouter Worker: reservation.rescinded')
        if (reservation && reservation.task) {
          self.$socket.emit('leave-task-room', reservation.task.sid)
        }
        self.$store.commit('reservation/RESET_WORKSPACE')
        bus.$emit('reservationInvalid')
        self.stopAudio()
        self.incomingCallNotification?.close()
      })
      worker.on('reservation.canceled', async function (reservation) {
        console.log('TaskRouter Worker: reservation.cancelled')
        if (reservation && reservation.task) {
          self.$socket.emit('leave-task-room', reservation.task.sid)
        }
        self.$store.commit('reservation/RESET_WORKSPACE')
        self.stopAudio()
        self.incomingCallNotification?.close()
        await bus.$emit('reservationCanceled', reservation.sid)
        if (self.$route.name === 'Calling') return
        localStorage.setItem('reservationCanceled', reservation.sid)
      })
      worker.on('reservation.rejected', function (reservation) {
        console.log('TaskRouter Worker: reservation.rejected')
        if (reservation && reservation.task) {
          self.$socket.emit('leave-task-room', reservation.task.sid)
        }
        self.$store.commit('reservation/RESET_WORKSPACE')
        self.stopAudio()
        self.changeStatusTaskRouter(self.onlineStatus)
        self.incomingCallNotification?.close()
      })
      worker.on('reservation.completed', function (reservation) {
        console.log('TaskRouter Worker: reservation.completed')
        if (reservation && reservation.task) {
          self.$socket.emit('leave-task-room', reservation.task.sid)
        }
        self.$store.commit('reservation/RESET_WORKSPACE')
        self.stopAudio()
        bus.$emit('reservationCompleted', reservation.sid)
        if (localStorage.getItem('lastStatus') && localStorage.getItem('reservationClosed')) {
          self.changeStatusTaskRouter(self.onlineStatus)
        }
        self.incomingCallNotification?.close()
      })
      worker.on('task.wrapup', function (reservation) {
        bus.$emit('taskWrapUp')
      })
      worker.on('task.completed', function (reservation) {
        console.log('TaskRouter Worker: task.completed')
        if (reservation && reservation.task) {
          self.$socket.emit('leave-task-room', reservation.task.sid)
        }
        self.$store.commit('reservation/RESET_WORKSPACE')
        self.stopAudio()
        self.incomingCallNotification?.close()
      })
      worker.on('activity.update', function (worker) {
        self.$store.commit('reservation/SET_WORKER', worker)
        console.log(
          `TaskRouter Worker: activity.update, new activity is ${worker.activitySid} - "${worker.activityName}"`
        )
        if (
          self.offlineStatus &&
          self.inCallStatus &&
          this.holdingStatus &&
          worker.activitySid !== self.offlineStatus.sid && worker.activitySid !== self.inCallStatus.sid &&
          worker.activitySid !== this.holdingStatus.sid
        ) {
          self.stopAudio()
          localStorage.setItem('lastStatus', worker.activitySid)
        }
        self.$socket.emit('change-status')
      })
      worker.on('token.expired', async function () {
        console.log('TaskRouter Worker: token.expired')

        // redirect to Login if token expired
        const storage = localStorage.getItem('remember') ? localStorage : sessionStorage
        const tokenExpired = JSON.parse(storage.getItem('tokenExpired'))
        if (tokenExpired && tokenExpired < moment().valueOf()) {
          console.log('System token expired')
          return await self.$router.push({ name: 'Login', params: { alert: true } })
        }

        if (self.isRefreshingToken) return
        self.isRefreshingToken = true
        const newToken = await self.refreshTwilioToken()
        if (!newToken) return
        self.workerTaskRouter.updateToken(newToken.taskRouterToken)
        self.setupListenerTaskRouter(self.workerTaskRouter)
        console.log('TaskRouter Worker: token.expired has refreshed')
        await twilioDevice.setup(newToken.twilioClientAccessToken, { debug: true })
        self.isRefreshingToken = false
        self.isTwilioInitialized = true
      })
      worker.on('connected', function () {
        console.log('TaskRouter Worker: WebSocket has connected')
        clearInterval(this.disconnectTaskRouterInterval)
        self.hideDisconnectedModal()
      })
      worker.on('disconnected', async function () {
        self.stopAudio()
        console.log('debug log: taskrouter worker disconnected')
        await self.closeCallingAndShowModal()
        await self.reconnectTaskRouter()
        console.log('TaskRouter Worker: WebSocket has disconnected')
      })
      worker.on('error', async function (error) {
        console.log(error)
        console.log(
          'TaskRouter Worker: an error occurred: ' +
            error.response +
            ' with message: ' +
            error.message
        )
        console.log('debug log: taskrouter worker has an error')
        await self.closeCallingAndShowModal()
        await self.reconnectTaskRouter()
      })
    },
    async reconnectTaskRouter(reconnect = false) {
      if (!reconnect) return
      if (window.navigator.onLine) {
        clearInterval(this.disconnectTaskRouterInterval)
        this.disconnectTaskRouterInterval = setInterval(async () => {
          this.setupTaskRouter(true)
        }, 3 * 1000)
      }
    },
    async refreshTwilioToken () {
      const result = await UserRepository.refreshTwilioToken()
      if (result.status_code === 200) {
        const newToken = {
          twilioClientAccessToken: result.twilio_client_access_token,
          taskRouterToken: result.task_router_token
        }
        await this.$store.commit('user/REFRESH_TWILIO_TOKEN', newToken)
        return newToken
      }
      return false
    },
    changeStatusTaskRouter(status) {
      try {
        const self = this
        if (!status || !status.sid) {
          return
        }
        const payload = {
          ActivitySid: status.sid
          // 'RejectPendingReservations': true
        }
        this.workerTaskRouter.update(payload, function (error, worker) {
          if (worker) {
            if (status.sid !== self.offlineStatus.sid && status.sid !== self.inCallStatus.sid && status.sid !== self.holdingStatus.sid) {
              localStorage.setItem('lastStatus', status.sid)
            }
            if (self.disconnectSocketInterval) {
              clearInterval(self.disconnectSocketInterval)
            }
            bus.$emit('changeStatus')
            self.$socket.emit('change-status')
            self.isTwilioInitialized = true
            self.$store.commit('reservation/RESET_INVALID_RESERVATION')
          }
          if (error) {
            if (error?.message && error.message.includes('pending reservation') && status?.onClick) {
              self.$swal.fire({
                title: '',
                text: '着信中にステータス「受付可」から他のステータスに変更することはできません。',
                icon: 'error',
                confirmButtonText: '閉じる'
              })
            }
            console.log('changeStatusTaskRouter error', error)
          }
        })
      } catch (e) {
        console.log('changeStatusTaskRouter e ', e)
        return e.message
      }
    },
    completeTask () {
      const task = this.$store.getters['reservation/task']
      if (task) {
        this.workerTaskRouter.completeTask(task.sid, function (error, task) {
          if (error) {
            console.log(error)
            return
          }
          console.log(`TaskRouter Worker: Completed Task: ${task.sid}`)
        })
      }
    },
    async initAudio () {
      try {
        this.audio = new Audio('audio/telephone.mp3')
        this.audio.loop = true
      } catch (e) {
        console.log('Init audio fail')
      }
    },
    playAudio() {
      try {
        this.audio.play()
      } catch (e) {
        console.log('Play audio fail')
      }
    },
    stopAudio () {
      try {
        this.audio.pause()
        this.audio.currentTime = 0
      } catch (e) {
        console.log('Stop audio fail')
      }
    },
    endIncomingCall () {
      this.completeTask()
    },
    parseJwt (token) {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      }).join(''))

      return JSON.parse(jsonPayload)
    },
    async getPhoneAccount (caller) {
      const response = await PhoneAccountRepository.getPhoneNumber(caller)
      const phoneNumber = caller.replace('+', '').replace(/^81/g, '0')
      return response.status_code === 200 ? response.data.name : phoneNumber
    },
    async logout (otherDeviceLogin = false, inactive = false) {
      this.$socket.emit('leave-room', `company_${this.userInfo.company_id}`)
      await UserRepository.updateIsMustLogoutStatus(this.userInfo.id, true)
      await this.$socket.emit('offlineSocket')
      if (!otherDeviceLogin) {
        await this.changeStatusTaskRouter(this.offlineStatus)
      }
      await this.$store.dispatch('user/logout')
      this.stopAudio()
      await this.destroyTwilio()
      // reset workspace
      if (this.currentReservation && this.currentReservation.task) {
        this.$socket.emit('leave-task-room', this.currentReservation.task.sid)
      }
      await this.$store.commit('reservation/RESET_WORKSPACE')
      await this.$store.commit('reservation/RESET_WORKER')
      await this.$store.commit('twilioDevice/REMOVE_TWILIO_DEVICE')
      await this.$store.commit('callMonitoring/SET_COACHING_CONFERENCE')
      this.isSetupTwilioEvent = false
      this.isSetupTaskRouterEvent = false

      localStorage.setItem('logout', true)
      localStorage.removeItem('login')
      localStorage.removeItem('userCompany')
      localStorage.removeItem('lastStatus')
      localStorage.removeItem('end_call_status')
      this.twilioInitializing = false

      if (inactive) {
        this.$router.push({ name: 'Login', params: { otherDeviceLogin: false, inactive: inactive } })
      } else {
        if (this.$route.path !== '/login') {
          this.$router.push({ name: 'Login', params: { otherDeviceLogin: otherDeviceLogin } })
        }
      }
    },
    formatTextLong(str, n) {
      if (str) {
        const newStr = str.replace(/(?:\n|\r\n|\r)/g, '')
        return (newStr.length > n) ? newStr.substr(0, n - 1) + '...' : newStr
      }
      return ''
    },
    showNewMessageNotification (data) {
      const body = `\n${data.last_message.type === 'text' ? '「' + this.formatTextLong(data.last_message.message, 15) + '」' : '(ファイルが送信されました)'}`
      Helper.displayNotification(`${data.name ? data.name + '(' + data.line_name + ')' : data.line_name}からのメッセージ`, { body }, data)
    },
    ...mapMutations({
      setHasModalNetworkError: 'common/SET_HAS_MODAL_NETWORK_ERROR'
    })
  }
}
</script>
<style src="./assets/css/reset.css"></style>
<style src="@fortawesome/fontawesome-free/css/all.min.css"></style>
